import { useCallback, useMemo, useRef, useState } from 'react';

import {
  useApolloClient,
  useFragment as useApolloFragment,
} from '@apollo/client/react';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  List,
  ListItem,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import {
  calculateCaprateMinimumRequiredYield,
  estimateMonthlyNetRent,
  hasRentComparison,
  readRentalComparables,
  resolveMonthlyRent,
} from '@realadvisor/listings/rental-cma';
import { Rows3Icon } from 'lucide-react';

import {
  type FormDefinitionType,
  RaForm,
  type RaFormOnChange,
} from '@modules/_shared/components/form/RaForm';
import {
  type ReportComponentProps,
  useReportContext,
} from '@modules/_shared/components/report-editor/ReportEditorContext';
import { getCurrencySymbol } from '@modules/_shared/utils/formatting';
import { STEP_CAP_RATE_VALUATION_FRAGMENT } from '@modules/reports/cma-reports/cmaReportsQueries';
import {
  fetchRentalComparables,
  getRentalCohortsSearch,
} from '@modules/reports/cma-reports/step-listing-compare/rentalComparablesSearch';
import { FooterActions } from '@modules/reports/shared/FooterActionsComponentWithoutForm';
import { useUpdateCmaReport } from '@modules/reports/shared/utils';
import { useDebouncedHandler } from '@shared/hooks/debounce';
import { useLocale } from '@shared/hooks/locale';
import { getCurrencyByCountryCode } from '@shared/LocaleContext';
import { formatPrice } from '@shared/utils/format-price';

type StepCapRateValuationForm = {
  caprate_estimated_monthly_rent: number | null | undefined;
  caprate_minimum_required_yield: number | null | undefined;
};

type MonthlyRentSourcesProps = {
  rents: ReturnType<typeof resolveMonthlyRent>;
  formatRent: (amount: number | null) => string;
};

const MonthlyRentSources = ({ rents, formatRent }: MonthlyRentSourcesProps) => {
  const { t } = useLocale();

  return (
    <List sx={{ py: 0 }}>
      <ListItem divider disableGutters sx={{ justifyContent: 'space-between' }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {t('AVM estimated monthly rent')}
        </Typography>
        <Typography variant="body2">{formatRent(rents.avm)}</Typography>
      </ListItem>
      <ListItem divider disableGutters sx={{ justifyContent: 'space-between' }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {t('Comparable rental listings estimate')}
        </Typography>
        <Typography variant="body2">{formatRent(rents.comparables)}</Typography>
      </ListItem>
      <ListItem disableGutters sx={{ justifyContent: 'space-between' }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {t('Suggested monthly rent')}
        </Typography>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {formatRent(rents.suggested)}
        </Typography>
      </ListItem>
    </List>
  );
};

type RentalComparablesPromptProps = {
  mode: 'enable' | 'enable-saved' | 'fetch';
  loading: boolean;
  noResults: boolean;
  missingSurface: boolean;
  onClick: () => void;
};

const RentalComparablesPrompt = ({
  mode,
  loading,
  noResults,
  missingSurface,
  onClick,
}: RentalComparablesPromptProps) => {
  const { t } = useLocale();

  return (
    <Stack
      sx={{
        gap: 1.5,
        p: 2,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        alignItems: 'flex-start',
      }}
    >
      <Typography variant="subtitle2">
        {t('Improve your monthly rent estimate')}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {mode === 'fetch'
          ? t(
              'Fetch comparable rental listings to find similar rented properties and include them in the valuation.',
            )
          : t(
              'Enable comparable rental listings to find similar rented properties and include them in the valuation.',
            )}
      </Typography>
      {missingSurface && (
        <Alert severity="info" sx={{ alignSelf: 'stretch' }}>
          {t(
            'Add a surface area to the property to compute the estimated rent.',
          )}
        </Alert>
      )}
      {noResults && (
        <Alert severity="info" sx={{ alignSelf: 'stretch' }}>
          {t('No comparable rental listings were found.')}
        </Alert>
      )}
      <LoadingButton
        variant="contained"
        startIcon={<Rows3Icon />}
        loading={loading}
        loadingPosition="start"
        onClick={onClick}
      >
        {mode === 'fetch'
          ? t('Fetch rental comparables')
          : mode === 'enable-saved'
          ? t('Enable rental comparables')
          : t('Enable and fetch rental comparables')}
      </LoadingButton>
    </Stack>
  );
};

const StepCapRateValuation = ({
  reportId: cmaReportId,
  ...props
}: ReportComponentProps) => {
  const { locale, countryCode } = useLocale();
  const { setError } = useReportContext();
  const client = useApolloClient();
  const [fetchingRentals, setFetchingRentals] = useState(false);
  const [noRentalResults, setNoRentalResults] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const { data, complete } = useApolloFragment({
    fragment: STEP_CAP_RATE_VALUATION_FRAGMENT,
    fragmentName: 'StepCapRateValuation',
    from: { __typename: 'cma_reports', id: cmaReportId },
  });

  const latestAppraisal = data?.property?.latest_appraisal;
  const savedRentals = useMemo(
    () => readRentalComparables(data?.rental_comparables),
    [data?.rental_comparables],
  );
  const includeRentals = data?.include_rental_comparables === true;
  const rentalSearch = useMemo(
    () => getRentalCohortsSearch(data?.property, countryCode),
    [data?.property, countryCode],
  );
  const missingSurface =
    data?.property?.computed_surface == null ||
    data.property.computed_surface <= 0;
  const rentalPrompt = !includeRentals
    ? savedRentals.length > 0
      ? 'enable-saved'
      : 'enable'
    : savedRentals.length === 0
    ? 'fetch'
    : null;
  const rents = resolveMonthlyRent({
    avm: latestAppraisal?.rent_value,
    comparables: estimateMonthlyNetRent(
      savedRentals,
      data?.property?.computed_surface,
    ),
    includeComparables: includeRentals,
    override: data?.caprate_estimated_monthly_rent,
  });
  const showRentComparison = hasRentComparison(rents);
  const pending = useRef<Partial<StepCapRateValuationForm>>({});
  const formatRent = useCallback(
    (amount: number | null) =>
      formatPrice(
        amount,
        locale,
        getCurrencyByCountryCode(countryCode),
        0,
        '—',
      ),
    [countryCode, locale],
  );

  const [updateCmaReport, updating] = useUpdateCmaReport(
    cmaReportId,
    'page-cap-rate-valuation',
  );

  const update = useCallback(async () => {
    const changes = pending.current;
    pending.current = {};
    if (Object.keys(changes).length > 0) {
      try {
        const result = await updateCmaReport(changes);
        if (result.error) {
          throw result.error;
        }
      } catch (error) {
        pending.current = { ...changes, ...pending.current };
        throw error;
      }
    }
  }, [updateCmaReport]);

  const enableRentalComparables = useCallback(async () => {
    if (fetchingRentals) {
      return;
    }
    setFetchingRentals(true);
    try {
      await update();
      const rentals =
        savedRentals.length > 0
          ? savedRentals
          : await fetchRentalComparables(client, rentalSearch.variables);
      const result = await updateCmaReport({
        include_rental_comparables: true,
        rental_comparables: rentals,
      });
      if (result.error) {
        throw result.error;
      }
      setNoRentalResults(rentals.length === 0);
      setFormKey(key => key + 1);
    } catch (error) {
      setError(error instanceof Error ? error : new Error(String(error)));
    } finally {
      setFetchingRentals(false);
    }
  }, [
    client,
    fetchingRentals,
    rentalSearch.variables,
    savedRentals,
    setError,
    update,
    updateCmaReport,
  ]);

  const debouncedUpdate = useDebouncedHandler(300, () => {
    void update().catch(() => undefined);
  });

  const onChangeHandler: RaFormOnChange<StepCapRateValuationForm> = useCallback(
    (formData, name, type) => {
      if (type !== 'change' || !name) {
        return;
      }
      pending.current[name] = formData[name] ?? null;
      debouncedUpdate();
    },
    [debouncedUpdate],
  );

  const stepCapRateValuationFormDefinition = useCallback<
    FormDefinitionType<StepCapRateValuationForm>
  >(
    ({ t }) => [
      ...(rentalPrompt != null
        ? [
            {
              type: 'custom' as const,
              name: 'rental-comparables-prompt',
              gridProps: { md: 12 },
              element: (
                <RentalComparablesPrompt
                  mode={rentalPrompt}
                  loading={fetchingRentals}
                  noResults={noRentalResults && rentalPrompt === 'fetch'}
                  missingSurface={missingSurface}
                  onClick={() => void enableRentalComparables()}
                />
              ),
            },
          ]
        : []),
      ...(showRentComparison
        ? [
            {
              type: 'category-title' as const,
              name: 'estimated-monthly-rent',
              label: t('Estimated monthly rent'),
              gridProps: { md: 12 },
            },
            {
              type: 'custom' as const,
              name: 'monthly-rent-sources',
              gridProps: { md: 12 },
              element: (
                <MonthlyRentSources rents={rents} formatRent={formatRent} />
              ),
            },
          ]
        : []),
      {
        name: 'caprate_estimated_monthly_rent',
        label: showRentComparison
          ? t('Final estimated monthly rent')
          : t('Estimated monthly rent'),
        gridProps: { md: 12 },
        type: 'number',
        prefix: getCurrencySymbol(
          getCurrencyByCountryCode(countryCode),
          locale,
        ),
        min: 0,
        max: 999_999_999,
      },
      {
        name: 'caprate_minimum_required_yield',
        label: t('Rental yield'),
        type: 'number',
        min: 0,
        max: 100,
        suffix: '%',
        decimalNumbers: 2,
        gridProps: { md: 12 },
      },
    ],
    [
      countryCode,
      locale,
      rents,
      showRentComparison,
      formatRent,
      rentalPrompt,
      fetchingRentals,
      noRentalResults,
      missingSurface,
      enableRentalComparables,
    ],
  );

  const defaultValues = useMemo(() => {
    return {
      caprate_estimated_monthly_rent: rents.final,
      caprate_minimum_required_yield: calculateCaprateMinimumRequiredYield(
        data?.caprate_minimum_required_yield,
        latestAppraisal?.rent_value,
        latestAppraisal?.value,
      ),
    };
  }, [data, latestAppraisal, rents.final]);

  const onSubmit = async () => {
    await update();
    props.setStep(props.step + 1);
  };

  if (!complete) {
    return (
      <Stack
        sx={{
          gap: 4,
        }}
      >
        <Stack
          sx={{
            gap: 1,
          }}
        >
          <Skeleton variant="rounded" width={'30%'} height={30} />
          <Skeleton variant="rounded" width={'50%'} height={40} />
        </Stack>
        <Stack
          sx={{
            gap: 2,
          }}
        >
          <Skeleton variant="rounded" height={20} />
          <Stack direction={'row'}>
            <Skeleton
              variant="circular"
              width={24}
              height={24}
              sx={{ mr: 2 }}
            />
            <Skeleton variant="rounded" width={'30%'} height={24} />
          </Stack>
          <Stack
            sx={{
              gap: 3,
            }}
          >
            {Array.from({ length: 2 }).map((_, index) => (
              <Skeleton
                variant="rounded"
                key={`caprate-${index}`}
                height={50}
              />
            ))}
          </Stack>
        </Stack>
      </Stack>
    );
  }

  return (
    <RaForm
      key={formKey}
      freezeInitialDefaultValues={true}
      formDefinition={stepCapRateValuationFormDefinition}
      defaultValues={defaultValues}
      onChange={onChangeHandler}
      onSubmit={onSubmit}
      contentScrollable
      actionButtonsComponent={<FooterActions {...props} updating={updating} />}
    />
  );
};

export default StepCapRateValuation;
